'use client'
import HotelDateModifier from '@/src/components/common/hotels/review/hotelDateModifier'
import HotelDesktopPageShell from '@/src/components/common/hotels/pageShell/hotelDesktopPageShell'
import FullPageLoader from '@/src/components/common/ui/fullPageLoader'
import useCreateHotelItinerary from '@/src/hooks/hotels/useCreateHotelItinerary'
import { formatHotelCurrency } from '@/src/utils/hotels'
import useHotelFailureDialog from '@/src/hooks/hotels/useHotelFailureDialog'
import { ReviewTab } from '@/src/components/mobile/hotels/review/reviewTabBar'
import { HotelReviewViewModel } from '@/src/types/hotels/details'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useCallback, useMemo, useState } from 'react'
import HotelDetailsDesktop from './details'

/**
 * What a page must supply to render hotel details. The booking flow provides it from
 * useHotelReviewData; the property (SEO) page from usePropertyPageData. The two load
 * differently — one arrives priced from a search, the other from Google with no dates —
 * but everything below this point is identical, so it lives here once.
 */
export interface HotelDetailsDataSource {
    viewModel: HotelReviewViewModel | null
    locale: string
    isLoading: boolean
    isError: boolean
    isUpdatingDates: boolean
    /** Runs the search directly. The booking flow arrives priced and has none. */
    searchRooms?: () => void
    applyDateChange: (checkIn: Date, checkOut: Date) => Promise<boolean>
}

export interface HotelDetailsDesktopScreenProps {
    locale: string
    domainCountry?: string
    data: HotelDetailsDataSource
    /** Presentation the property (SEO) page needs; the booking flow passes none of it. */
    availableTabs?: ReviewTab[]
    defaultTab?: ReviewTab
    expandAll?: boolean
    searchLabel?: string
    /** Rooms-and-guests control. Only the property (SEO) page supplies one. */
    guestsField?: React.ReactNode
    breadcrumb?: React.ReactNode
    /** Let the guest apply the stay already on screen — the property page has not priced it. */
    allowUnchangedDates?: boolean
}

const HotelDetailsDesktopScreen = ({
    locale,
    domainCountry,
    data,
    availableTabs,
    defaultTab,
    expandAll,
    searchLabel,
    guestsField,
    breadcrumb,
    allowUnchangedDates
}: HotelDetailsDesktopScreenProps) => {
    const t = useTranslations('hotels')
    const router = useRouter()
    // Only `searchAgain` is needed here; the dialog itself belongs to the action steps.
    const { searchAgain } = useHotelFailureDialog(locale, false)
    const { viewModel, locale: activeLocale, isLoading, isError, isUpdatingDates, applyDateChange } = data

    const [pickerOpen, setPickerOpen] = useState(false)

    // Close the picker before the overlay goes up — one blocking layer at a time.
    const handleApplyDates = useCallback(
        (checkIn: Date, checkOut: Date) => {
            setPickerOpen(false)
            void applyDateChange(checkIn, checkOut)
        },
        [applyDateChange]
    )

    // Selecting a rate creates the Cleartrip itinerary and hands off its id; the id is the
    // whole address of the booking, so nothing travels in the query string.
    const { selectRoom: handleSelectRoom, isCreating } = useCreateHotelItinerary({
        viewModel,
        locale,
        domainCountry: domainCountry ?? ''
    })

    // "from {price}" for the sticky booking rail = the cheapest rate across all rooms.
    // Format with that rate's own currency — `viewModel.currency` is the supplier
    // currency, while `price.now` is already converted to the user's.
    const fromPriceText = useMemo(() => {
        if (!viewModel) return ''
        const prices = viewModel.roomGroups.flatMap(group => group.options.map(option => option.price))
        if (prices.length === 0) return ''
        const cheapest = prices.reduce((min, price) => (price.now < min.now ? price : min))
        return formatHotelCurrency(cheapest.now, cheapest.currency, activeLocale)
    }, [viewModel, activeLocale])

    return (
        <HotelDesktopPageShell identifier="hotel-details-desktop" domainCountry={domainCountry}>
            <HotelDetailsDesktop
                availableTabs={availableTabs}
                defaultTab={defaultTab}
                expandAll={expandAll}
                searchLabel={searchLabel}
                onSearch={data.searchRooms}
                guestsField={guestsField}
                breadcrumb={breadcrumb}
                viewModel={viewModel}
                isLoading={isLoading}
                isError={!isLoading && (isError || !viewModel)}
                errorTitle={t('reviewErrorTitle')}
                errorSubtitle={t('reviewErrorSubtitle')}
                errorCtaLabel={t('searchAgain')}
                onErrorAction={searchAgain}
                tabLabels={{
                    rooms: t('roomsAndRates'),
                    info: t('hotelAmenitiesInfo'),
                    map: t('mapView')
                }}
                photosCountLabel={count => t('photosCount', { count })}
                viewPhotosLabel={t('viewPhotos')}
                fromPriceText={fromPriceText}
                fromLabel={t('total')}
                selectRoomLabel={t('selectRoom')}
                onEditDates={viewModel ? () => setPickerOpen(true) : undefined}
                datePicker={
                    viewModel && (
                        <HotelDateModifier
                            variant="desktop"
                            open={pickerOpen}
                            stayCheckIn={viewModel.stay.checkIn}
                            stayCheckOut={viewModel.stay.checkOut}
                            onClose={() => setPickerOpen(false)}
                            onApply={handleApplyDates}
                            allowUnchanged={allowUnchangedDates}
                            guestsField={guestsField}
                        />
                    )
                }
                onSelectRoom={handleSelectRoom}
            />
            {(isUpdatingDates || isCreating) && <FullPageLoader message={t('pleaseWait')} />}
        </HotelDesktopPageShell>
    )
}

export default HotelDetailsDesktopScreen
